package org.zest.insan_360_website.data.page_states
import org.zest.insan_360_website.data.localized

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.browser.storage.getItem
import com.varabyte.kobweb.browser.storage.removeItem
import com.varabyte.kobweb.browser.storage.setItem
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import kotlinx.browser.localStorage
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.zest.insan_360_website.components.widgets.ToastState
import org.zest.insan_360_website.components.widgets.ToastType
import org.zest.insan_360_website.data.LanguageSettings
import org.zest.insan_360_website.data.page_states.CreditState.creditState
import org.zest.insan_360_website.data.page_states.ResultPageState.hasSubmitted
import org.zest.insan_360_website.model.*
import org.zest.insan_360_website.pages.Answer
import org.zest.insan_360_website.pages.USER_ANSWERS_KEY
import org.zest.insan_360_website.utils.NetworkUtils
import org.zest.insan_360_website.utils.processResponse

object AssessmentPageState {
    val answers = mutableStateListOf<Answer>().apply {
        val savedAnswers = localStorage.getItem(USER_ANSWERS_KEY) ?: List(24) { Answer(null, null) }
        addAll(savedAnswers)
    }
    var index by mutableStateOf(0)

    fun reset() {
        answers.clear()
        answers.addAll(List(24) { Answer(null, null) })
        index = 0
        localStorage.removeItem(USER_ANSWERS_KEY)
    }

    fun updateAnswer(questionIndex: Int, answer: Answer) {
        answers[questionIndex] = answer
        localStorage.setItem(USER_ANSWERS_KEY, answers.toList())
    }

    enum class SubmitTestResult {
        SUCCESS,
        NETWORK_ERROR,
        FATAL_ERROR,
        AUTH_EXPIRED
    }

    suspend fun submitTest(
        toast: ToastState,
        plotMore: Map<Attribute, Int>,
        plotLess: Map<Attribute, Int>,
        plotDiff: Map<Attribute, Int>,
        onSuccess: suspend () -> Unit,
    ): SubmitTestResult {
        val email = AuthState.email
        val token = AuthState.token
        if (email == null || token == null) {
            toast.show("User auth not found. Cannot submit report.", ToastType.ERROR)
            return SubmitTestResult.FATAL_ERROR
        }

        return try {
            val resultData = TestResultData(
                graphs = mapOf(
                    GraphType.More to plotMore,
                    GraphType.Less to plotLess,
                    GraphType.Difference to plotDiff
                )
            )
            val request = SubmitTestRequest(email = email, results = resultData)
            val response = NetworkUtils.postSecureApi("/api/test/submit", request)
                ?: return SubmitTestResult.AUTH_EXPIRED

            var isSuccess = false
            processResponse<Unit>(response, toast, "Failed to submit test results.") {
                onSuccess()
                hasSubmitted = true
                isSuccess = true
            }

            if (isSuccess) {
                SubmitTestResult.SUCCESS
            } else if (response.status >= 500) {
                SubmitTestResult.NETWORK_ERROR
            } else {
                SubmitTestResult.FATAL_ERROR
            }
        } catch (e: Throwable) {
            console.error(e.message)
            SubmitTestResult.NETWORK_ERROR
        }
    }

    fun terminateUnfulfilledAssessment(
        scope: CoroutineScope, 
        toast: ToastState, 
        ctx: PageContext,
        onComplete: () -> Unit = {}
    ) {
        if (creditState == CreditStatus.IN_PROGRESS) {
            val email = AuthState.email ?: run { onComplete(); return }
            if (AuthState.token == null) { onComplete(); return }

            toast.show("Terminating the test...", ToastType.INFO)

            scope.launch {
                try {
                    val request = ChangeCreditStatusRequest(
                        email = email,
                        changeTo = CreditStatus.USED
                    )
                    val response = NetworkUtils.patchSecureApi("/api/credit/change-status", request)
                        ?: throw Exception(
                            LanguageSettings.ResponseError.session_expired.localized
                        )

                    processResponse<Unit>(response, toast, "Unable to terminate assessment") {
                        ctx.router.navigateTo("/user", UpdateHistoryMode.REPLACE)
                        reset()
                    }

                } catch (e: Throwable) {
                    toast.show(e.message ?: "Network Error", ToastType.ERROR)
                } finally {
                    onComplete()
                }
            }
        } else {
            toast.show("Error terminating test: credit state misalignment $creditState", ToastType.ERROR)
            onComplete()
        }
    }
}